import { useEffect } from "react";
import { Flex } from "@chakra-ui/react";
// Shared Layout
import Menu from "./shared/layout/Menu/Menu";
import Footer from "./shared/layout/Footer/Footer";
import ProtectedRoute from "./shared/layout/ProtectedRoute/ProtectedRoute";
import { useAuth } from "./shared/providers/AuthProvider/AuthProvider";

import { Route, Routes, useLocation } from "react-router-dom";

// Features - Auth
import LoginPage from "./features/auth/pages/LoginPage/LoginPage";
import RegisterPage from "./features/auth/pages/RegisterPage/RegisterPage";
import RegisterSuccess from "./features/auth/pages/RegisterSuccess/RegisterSuccess";
import ActivationPage from "./features/auth/pages/ActivationPage/ActivationPage";
import ResetPasswordRequestPage from "./features/auth/pages/ResetPasswordRequest/ResetPasswordRequestPage";
import ResetPasswordConfirmPage from "./features/auth/pages/ResetPasswordConfirm/ResetPasswordConfirmPage";

// Features - Events
import EventDetailPage from "./features/events/pages/EventDetailPage/EventDetailPage";
import EventsTable from "./features/events/pages/EventsTable/EventsTable";
import AddEventForm from "./features/events/pages/AddEventForm/AddEventForm";
import EditEventForm from "./features/events/pages/EditEventForm/EditEventForm";
import CategoriesPage from "./features/events/pages/CategoriesPage/CategoriesPage";
import AllEventsPage from "./features/events/pages/AllEventsPage/AllEventsPage";
import EventParticipantsPage from "./features/events/pages/EventParticipantsPage/EventParticipantsPage";

// Features - Setup
import RequireAccountSetup from "./features/setup/components/RequireAccountSetup/RequireAccountSetup";
import AccountSetupPage from "./features/setup/pages/AccountSetupPage/AccountSetupPage";

// Features - Cart
import MyCart from "./features/cart/pages/MyCart/MyCart";
import OrderFlow from "./features/cart/pages/OrderFlow/OrderFlow";

import UsersListPage from "./pages/UsersList/UsersListPage";
import UserDetailPage from "./pages/UserDetail/UserDetailPage";

// Features - Static Pages
import Contact from "./features/static-pages/pages/Contact/Contact";
import Purchase from "./features/static-pages/pages/Purchase/Purchase";
import Returns from "./features/static-pages/pages/Returns/Returns";
import Payments from "./features/static-pages/pages/Payments/Payments";
import Deliveries from "./features/static-pages/pages/Deliveries/Deliveries";
import Cooperation from "./features/static-pages/pages/Cooperation/Cooperation";
import Organizers from "./features/static-pages/pages/Organizers/Organizers";
import About from "./features/static-pages/pages/About/About";
import Regulations from "./features/static-pages/pages/Regulations/Regulations";
import Cookie from "./features/static-pages/pages/Cookie/Cookie";
import Statute from "./features/static-pages/pages/Statute/Statute";
import PrivacyPolicy from "./features/static-pages/pages/PrivacyPolicy/PrivacyPolicy";


function App() {
    const { user } = useAuth();
    const location = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const isPreviewPage = /^\/events\/[^/]+\/preview$/.test(location.pathname);

    return (
        <Flex minH={'100vh'}
            flexDirection={'column'}
            alignItems={'center'}>
            {!isPreviewPage && <Menu />}
            {isPreviewPage && <Menu variant="preview" />}
            <Flex bg={'surface.page'} w={'100%'} justifyContent={'center'} pb={{ base: '16px', sm: '0px' }} flexGrow={1}>
                <Flex w={'100%'} alignItems="flex-start" justifyContent="center">
                    <Routes>
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute user={user}>
                                    <RequireAccountSetup>
                                        <EventsTable />
                                    </RequireAccountSetup>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/events/new"
                            element={
                                <ProtectedRoute user={user}>
                                    <RequireAccountSetup>
                                        <AddEventForm />
                                    </RequireAccountSetup>
                                </ProtectedRoute>
                            }
                        />
                        <Route path={"/login"} element={<LoginPage />} />
                        <Route path={"/register"} element={<RegisterPage />} />
                        <Route path={"/register-success"} element={<RegisterSuccess />} />
                        <Route path={"/activation/:key"} element={<ActivationPage />} />
                        <Route path={"//activation/:key"} element={<ActivationPage />} />
                        <Route path="/setup" element={<AccountSetupPage />} />
                        <Route
                            path="/events/:id/edit"
                            element={
                                <ProtectedRoute user={user}>
                                    <RequireAccountSetup>
                                        <EditEventForm />
                                    </RequireAccountSetup>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/events/:id/participants"
                            element={
                                <ProtectedRoute user={user}>
                                    <RequireAccountSetup>
                                        <EventParticipantsPage />
                                    </RequireAccountSetup>
                                </ProtectedRoute>
                            }
                        />
                        <Route path={"/categories"} element={<CategoriesPage />} />
                        <Route
                            path="/users"
                            element={
                                <ProtectedRoute user={user} requiredRole="SuperAdmin">
                                    <UsersListPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/users/:userId"
                            element={
                                <ProtectedRoute user={user} requiredRole="SuperAdmin">
                                    <UserDetailPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route path={"/all-events"} element={<AllEventsPage />} />
                        <Route path={"/events/:id/preview"} element={<EventDetailPage />} />
                        <Route path={"/contact"} element={<Contact />} />
                        <Route path={"/purchase"} element={<Purchase />} />
                        <Route path={"/returns"} element={<Returns />} />
                        <Route path={"/payments"} element={<Payments />} />
                        <Route path={"/deliveries"} element={<Deliveries />} />
                        <Route path={"/cooperation"} element={<Cooperation />} />
                        <Route path={"/organizers"} element={<Organizers />} />
                        <Route path={"/about"} element={<About />} />
                        <Route path={"/regulations"} element={<Regulations />} />
                        <Route path={"/cookie"} element={<Cookie />} />
                        <Route path={"/statute"} element={<Statute />} />
                        <Route path={"/privacy-policy"} element={<PrivacyPolicy />} />
                        <Route path={"/cart"} element={<MyCart />} />
                        <Route path={"/order"} element={<OrderFlow />} />
                        <Route path={"/reset-password"} element={<ResetPasswordRequestPage />} />
                        <Route path={"/reset-password/confirm"} element={<ResetPasswordConfirmPage />} />
                    </Routes>
                </Flex>
            </Flex>
            <Footer />
        </Flex>
    );
}

export default App;
